import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { epcEstimationService } from '@/services/epcEstimation.service';
import { formatMoney } from '@/utils/format';
import { EpcEstimateLabourBuildUpPanel } from './EpcEstimateLabourBuildUpPanel';
import { getEst3CostProvenance } from './est3CostProvenance';
import { EST3_LABOUR_RATE_BASES } from './est3LabourKinds';

const { Text } = Typography;

export interface EpcEstimateLabourWorkbenchProps {
  estimateId: string;
  revisionId: string;
  readOnly: boolean;
  labourLines: any[];
  loading?: boolean;
  onOpenAdd?: () => void;
  labourClasses: any[];
  crews: any[];
  productivityNorms: any[];
  onCostingChanged?: () => void;
}

/**
 * EST3 UX2 Wave 5 — Labour depth: master-detail lines + crew build-up + company library.
 */
export const EpcEstimateLabourWorkbench: React.FC<EpcEstimateLabourWorkbenchProps> = ({
  estimateId,
  revisionId,
  readOnly,
  labourLines,
  loading,
  onOpenAdd,
  labourClasses,
  crews,
  productivityNorms,
  onCostingChanged,
}) => {
  const qc = useQueryClient();
  const [selectedLineId, setSelectedLineId] = useState<string | null>(null);
  const [labourRateForm] = Form.useForm();
  const [crewCreateForm] = Form.useForm();
  const [prodCreateForm] = Form.useForm();

  const selectedLine = useMemo(
    () => labourLines.find((l) => l.id === selectedLineId) || null,
    [labourLines, selectedLineId]
  );

  const totalLabour = useMemo(
    () => labourLines.reduce((acc, l) => acc + (Number(l.total_amount) || 0), 0),
    [labourLines]
  );

  const crewCreateM = useMutation({
    mutationFn: (v: Record<string, unknown>) =>
      epcEstimationService.createCrewTemplate(estimateId, {
        code: v.code,
        name: v.name,
        description: v.description,
        members: v.resourceClassId
          ? [{ resourceClassId: v.resourceClassId, headcount: v.headcount ?? 1 }]
          : [],
      }),
    onSuccess: () => {
      crewCreateForm.resetFields();
      qc.invalidateQueries({ queryKey: ['epc-est-crews', estimateId] });
    },
  });

  const prodCreateM = useMutation({
    mutationFn: (v: Record<string, unknown>) =>
      epcEstimationService.createProductivityNorm(estimateId, v),
    onSuccess: () => {
      prodCreateForm.resetFields();
      qc.invalidateQueries({ queryKey: ['epc-est-prod', estimateId] });
    },
  });

  const labourRateM = useMutation({
    mutationFn: (v: Record<string, unknown>) => epcEstimationService.upsertLabourRate(estimateId, v),
    onSuccess: () => labourRateForm.resetFields(),
  });

  return (
    <Card size="small" title="Labour & Crews">
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {readOnly ? (
          <Alert type="info" showIcon message="Approved — labour read-only" />
        ) : null}

        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Statistic title="Labour lines" value={labourLines.length} />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title="Labour cost total"
              value={totalLabour}
              formatter={(v) => formatMoney(v, { display: 'plain', maximumFractionDigits: 0 })}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic title="Crew templates" value={crews.length} />
          </Col>
        </Row>

        {!readOnly && onOpenAdd ? (
          <Button type="primary" icon={<PlusOutlined />} onClick={onOpenAdd}>
            Add labour cost line
          </Button>
        ) : null}

        <Row gutter={16}>
          <Col xs={24} lg={10}>
            <Table
              size="small"
              rowKey="id"
              loading={loading}
              dataSource={labourLines}
              pagination={labourLines.length > 25 ? { pageSize: 25 } : false}
              rowSelection={{
                type: 'radio',
                selectedRowKeys: selectedLineId ? [selectedLineId] : [],
                onChange: (keys) => setSelectedLineId((keys[0] as string) || null),
              }}
              onRow={(row) => ({
                onClick: () => setSelectedLineId(row.id),
                style: { cursor: 'pointer' },
              })}
              columns={[
                { title: 'Description', dataIndex: 'description', ellipsis: true },
                { title: 'Prod.', dataIndex: 'productivity_factor', width: 70 },
                {
                  title: 'Total',
                  dataIndex: 'total_amount',
                  width: 100,
                  render: (v) => formatMoney(v, { display: 'plain', maximumFractionDigits: 0 }),
                },
                {
                  title: 'Prov.',
                  width: 100,
                  ellipsis: true,
                  render: (_: unknown, row: any) => {
                    const p = getEst3CostProvenance(row);
                    return p.summary !== '—' ? <Tag color="blue">{p.summary}</Tag> : '—';
                  },
                },
              ]}
            />
          </Col>
          <Col xs={24} lg={14}>
            <EpcEstimateLabourBuildUpPanel
              estimateId={estimateId}
              revisionId={revisionId}
              costLineId={selectedLineId}
              costLine={selectedLine}
              readOnly={readOnly}
              onChanged={onCostingChanged}
            />
          </Col>
        </Row>

        <Card type="inner" size="small" title="Company labour library">
          <Table
            size="small"
            rowKey="id"
            dataSource={labourClasses}
            pagination={labourClasses.length > 10 ? { pageSize: 10 } : false}
            columns={[
              { title: 'Code', dataIndex: 'code', width: 80 },
              { title: 'Name', dataIndex: 'name' },
              { title: 'UoM', dataIndex: 'default_uom', width: 80 },
            ]}
          />
          {!readOnly ? (
            <Space direction="vertical" style={{ width: '100%', marginTop: 12 }} size="middle">
              <Form form={labourRateForm} layout="inline" onFinish={(v) => labourRateM.mutate(v)}>
                <Form.Item name="resourceClassId" rules={[{ required: true }]}>
                  <Select
                    placeholder="Class"
                    style={{ width: 160 }}
                    options={labourClasses.map((c: any) => ({ value: c.id, label: c.code }))}
                  />
                </Form.Item>
                <Form.Item name="rateBasis" initialValue="NORMAL">
                  <Select
                    style={{ width: 110 }}
                    options={EST3_LABOUR_RATE_BASES.map((v) => ({ value: v, label: v }))}
                  />
                </Form.Item>
                <Form.Item name="rate" rules={[{ required: true }]}>
                  <InputNumber min={0} placeholder="Rate" />
                </Form.Item>
                <Button htmlType="submit" loading={labourRateM.isPending}>
                  Save rate
                </Button>
              </Form>
              <Form form={crewCreateForm} layout="inline" onFinish={(v) => crewCreateM.mutate(v)}>
                <Form.Item name="code" rules={[{ required: true }]}>
                  <Input placeholder="Crew code" style={{ width: 100 }} />
                </Form.Item>
                <Form.Item name="name" rules={[{ required: true }]}>
                  <Input placeholder="Crew name" style={{ width: 140 }} />
                </Form.Item>
                <Form.Item name="resourceClassId" rules={[{ required: true }]}>
                  <Select
                    placeholder="Member class"
                    style={{ width: 140 }}
                    options={labourClasses.map((c: any) => ({ value: c.id, label: c.code }))}
                  />
                </Form.Item>
                <Form.Item name="headcount" initialValue={1}>
                  <InputNumber min={0.1} placeholder="HC" />
                </Form.Item>
                <Button htmlType="submit" loading={crewCreateM.isPending}>
                  Create crew
                </Button>
              </Form>
              <Form form={prodCreateForm} layout="inline" onFinish={(v) => prodCreateM.mutate(v)}>
                <Form.Item name="code" rules={[{ required: true }]}>
                  <Input placeholder="Norm code" style={{ width: 100 }} />
                </Form.Item>
                <Form.Item name="name" rules={[{ required: true }]}>
                  <Input placeholder="Norm name" style={{ width: 140 }} />
                </Form.Item>
                <Form.Item name="outputQty" initialValue={1}>
                  <InputNumber min={0} placeholder="Output qty" />
                </Form.Item>
                <Form.Item name="defaultCrewTemplateId">
                  <Select
                    allowClear
                    placeholder="Default crew"
                    style={{ width: 140 }}
                    options={crews.map((c: any) => ({ value: c.id, label: c.code }))}
                  />
                </Form.Item>
                <Button htmlType="submit" loading={prodCreateM.isPending}>
                  Create norm
                </Button>
              </Form>
              <Text type="secondary">
                {productivityNorms.length} productivity norm{productivityNorms.length === 1 ? '' : 's'}{' '}
                available for crew snapshots.
              </Text>
            </Space>
          ) : null}
        </Card>
      </Space>
    </Card>
  );
};
